/**
 * Light-themed campaign registration disclosure (matches Active tournaments page).
 * Edition card: banner + description + Create a team. Continue/partner choices live on register-team.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaArrowRight, FaBullhorn } from 'react-icons/fa';
import { publicAssetUrl } from '../../utils/publicAssetUrl';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const Card = styled.article`
  width: 100%;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.05),
    0 12px 36px rgba(15, 23, 42, 0.08);
`;

const HeroBanner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 21 / 9;
  min-height: 140px;
  max-height: 300px;
  background: linear-gradient(135deg, #043663 0%, #0a5a9e 50%, #1e3a5f 100%);
  ${(p) =>
    p.$img
      ? `
    background-image: url(${p.$img});
    background-size: cover;
    background-position: center;
  `
      : ''}
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(15, 23, 42, 0.88) 0%,
      rgba(15, 23, 42, 0.35) 45%,
      transparent 72%
    );
    pointer-events: none;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 1rem clamp(1rem, 3vw, 1.5rem) 1.1rem;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.3rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.85);
`;

const HeroTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.15rem, 3vw, 1.65rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.12;
  color: #f8fafc;
`;

const CompactKicker = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
`;

const CompactTitle = styled.h3`
  margin: 0 0 0.65rem;
  font-size: clamp(1.2rem, 3vw, 1.65rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0f172a;
`;

const Body = styled.div`
  padding: 1.15rem clamp(1rem, 3vw, 1.75rem) 1.5rem;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 1.1rem;
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #475569;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const SeasonLine = styled.p`
  margin: 0 0 0.85rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
`;

const Desc = styled.div`
  font-size: 0.9375rem;
  line-height: 1.62;
  color: #64748b;
  margin: 0 0 1.1rem;
  white-space: pre-wrap;
`;

const PresenceLoading = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.55rem;
  max-width: 420px;
  margin: 0 auto;
`;

const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.9rem 1.35rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.98rem;
  text-decoration: none;
  color: #fff;
  background: ${BRAND.red};
  box-shadow: 0 8px 24px rgba(213, 43, 30, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(213, 43, 30, 0.36);
    color: #fff;
  }
`;

const BtnSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  color: ${BRAND.blue};
  background: #fff;
  border: 1.5px solid #cbd5e1;
  &:hover {
    border-color: ${BRAND.blue};
    background: #f8fafc;
  }
`;

const BtnGhost = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem;
  &:hover {
    color: ${BRAND.blue};
  }
`;

function formatMonthYearShort(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

export default function RegistrationCampaignPanel({
  campaign,
  tenantName,
  seasonLabel,
  presenceLoading = false,
  registerHref,
  onBack,
  backLabel,
  includeBanner = true,
}) {
  const { t } = useTranslation();
  const pt = 'playerTournamentsPage';
  const resolvedBackLabel = backLabel ?? t(`${pt}.backToEditions`);
  const bannerSrc = campaign?.banner_display_url ? publicAssetUrl(campaign.banner_display_url) : '';

  return (
    <Card>
      {includeBanner ? (
        <HeroBanner $img={bannerSrc}>
          <HeroOverlay>
            <HeroKicker>{t(`${pt}.currentEditionKicker`, { league: tenantName || 'League' })}</HeroKicker>
            <HeroTitle>{campaign?.title || t(`${pt}.editionFallback`)}</HeroTitle>
          </HeroOverlay>
        </HeroBanner>
      ) : null}
      <Body>
        {!includeBanner ? (
          <>
            <CompactKicker>{t(`${pt}.currentEditionKicker`, { league: tenantName || 'League' })}</CompactKicker>
            <CompactTitle>{campaign?.title || t(`${pt}.editionFallback`)}</CompactTitle>
          </>
        ) : null}

        <SeasonLine>
          <FaBullhorn style={{ marginRight: '0.35rem', opacity: 0.7, verticalAlign: '-0.08em' }} size={13} />
          {seasonLabel}
        </SeasonLine>

        <MetaRow>
          {formatMonthYearShort(campaign?.start_date) ? (
            <MetaItem>
              <FaCalendarAlt aria-hidden />
              {t(`${pt}.editionStarts`, { date: formatMonthYearShort(campaign.start_date) })}
            </MetaItem>
          ) : null}
        </MetaRow>

        {campaign?.description ? <Desc>{campaign.description}</Desc> : null}

        {presenceLoading ? (
          <PresenceLoading>{t(`${pt}.presenceChecking`)}</PresenceLoading>
        ) : null}

        <Actions>
          {registerHref ? (
            <BtnPrimary to={registerHref}>
              {t(`${pt}.createNewTeamShort`, 'Create a team')} <FaArrowRight size={14} aria-hidden />
            </BtnPrimary>
          ) : null}

          <BtnSecondary to="/player/teams">Go to My teams</BtnSecondary>
          {onBack ? (
            <BtnGhost type="button" onClick={onBack}>
              {resolvedBackLabel}
            </BtnGhost>
          ) : null}
        </Actions>
      </Body>
    </Card>
  );
}
